import { Component, DestroyRef, HostListener, inject, NgZone, OnInit, ViewChild } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { MessageService } from 'primeng/api';
import { AuthService } from '../auth/auth.service';
import { PermissionsService } from '../auth/permissions.service';
import { CommonService } from '../shared/common.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { LAST_VERSION } from '../shared/common.enums';
import { HomeUserToolComponent } from './home-user-tool/home-user-tool.component';
import { PURCHASE_MODULE_PRELOAD_PERMISSIONS } from '../shared/routing/preload-permissions';
import { BootCoordinatorService } from '../shared/boot/boot-coordinator.service';

@Component({
  standalone: false,
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  providers: [MessageService]
})
export class HomeComponent implements OnInit {
  private readonly mobileBackGuardStateKey = 'sbestMobileSidebarGuard';
  private readonly sidebarCompactStorageKey = 'sbestSidebarCompact';
  private readonly mobileBreakpoint = 992;
  private readonly mobileSwipeEdge = 48;
  private readonly horizontalSwipeThreshold = 120;
  private readonly maxVerticalSwipeOffset = 80;
  private readonly moduleNavigationOrder = [
    { access: 'panel', route: '/panel-operativo' },
    { access: 'arqueo', route: '/arqueo' },
    { access: 'rtm', route: '/agenda' },
    { access: 'history', route: '/historial' },
    { access: 'users', route: '/usuarios' },
    { access: 'admin', route: '/administracion' },
    { access: 'prices', route: '/precios' },
    { access: 'sales', route: '/ventas' },
    { access: 'inventory', route: '/inventario' },
    { access: 'inventoryCatalog', route: '/inventario-consulta' },
    { access: 'salesbinder', route: '/sincronizacion-salesbinder' }
  ];

  private authService = inject(AuthService);
  private perms = inject(PermissionsService);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private ngZone = inject(NgZone);
  private destroyRef = inject(DestroyRef);
  private bootCoordinator = inject(BootCoordinatorService);
  public commonService = inject(CommonService);

  @ViewChild(HomeUserToolComponent) userToolComponent!: HomeUserToolComponent;

  readonly latestVersion = LAST_VERSION;
  dashboardIcon = 'speed';
  usersIcon = 'manage_accounts';
  historyIcon = 'history';
  arqueoIcon = 'balance';
  rtmIcon = 'route';
  tagsIcon = 'sell';
  adminIcon = 'account_tree';
  ventasIcon = 'point_of_sale';
  pricesIcon = 'swap_horiz';
  invetoryIcon = 'inventory_2';
  comprasIcon = 'local_shipping';
  visualIcon = 'photo_camera';
  salesBinderIcon = 'sync';
  fullSize = false;
  isSidebarCompact = false;
  menuSearchTerm = '';
  sidenavMode: 'side' | 'over' = 'side';
  isImmersiveMobileRoute = false;

  private touchStartX: number | null = null;
  private touchStartY: number | null = null;
  private swipeGestureActive = false;

  modulesAccess = {
    admin: false,
    panel: false,
    arqueo: false,
    rtm: false,
    users: false,
    visual: false,
    history: false,
    tags: false,
    prices: false,
    sales: false,
    inventory: false,
    inventoryCatalog: false,
    purchases: false,
    salesbinder: false,
  };

  ngOnInit(): void {
    this.commonService.currentStore$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (store) => {
        if (this.commonService.logos[store]) {
          this.commonService.currentLogo = this.commonService.logos[store];
        }
      }
    });

    this.setSidenavMode();
    this.restoreSidebarPreference();
    this.syncImmersiveMobileRoute();
    this.setModulesAccess();
    this.commonService.messageServiceRef = this.messageService;

    if (this.authService.loggedIn) {
      if (this.router.url === "/") {
        this.navigateToFirstAvailableModule();
      }
    } else {
      this.router.navigate(["/login"]);
    }

    this.commonService.fullSize$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (fullSize) => {
        this.fullSize = fullSize;
      }
    });

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => {
        this.syncImmersiveMobileRoute();
        this.syncMobileBackNavigationGuard();
      }
    });

    this.syncMobileBackNavigationGuard();
    this.bootCoordinator.markFirstAuthorizedRouteReady();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.setSidenavMode();
    this.syncImmersiveMobileRoute();
    this.syncMobileBackNavigationGuard();
  }

  @HostListener('window:popstate')
  onWindowPopState(): void {
    if (!this.shouldInterceptMobileBackNavigation()) {
      return;
    }
    this.commonService.setFullSize(false);
    this.pushMobileBackNavigationGuard();
  }

  onSidenavOpenedChange(opened: boolean): void {
    const nextFullSize = !opened;
    if (this.commonService.fullSize$.value !== nextFullSize) {
      this.commonService.setFullSize(nextFullSize);
    }
  }

  toggleSidebarCompact(): void {
    if (this.sidenavMode !== 'side') {
      return;
    }

    this.isSidebarCompact = !this.isSidebarCompact;
    this.menuSearchTerm = '';
    this.persistSidebarPreference();
  }

  updateMenuSearch(event: Event): void {
    this.menuSearchTerm = (event.target as HTMLInputElement).value;
  }

  isMenuItemVisible(label: string): boolean {
    const searchTerm = this.normalizeMenuText(this.menuSearchTerm.trim());
    return !searchTerm || this.normalizeMenuText(label).includes(searchTerm);
  }

  @HostListener('document:pointerdown', ['$event'])
  onPointerDown(event: PointerEvent): void {
    if (!this.isMobileTouchGesture(event) || this.swipeGestureActive) {
      this.resetTouchGesture();
      return;
    }
    this.swipeGestureActive = true;
    this.touchStartX = event.clientX;
    this.touchStartY = event.clientY;
  }

  @HostListener('document:pointerup', ['$event'])
  onPointerUp(event: PointerEvent): void {
    if (!this.swipeGestureActive || this.touchStartX === null || this.touchStartY === null) {
      this.resetTouchGesture();
      return;
    }
    const deltaX = event.clientX - this.touchStartX;
    const deltaY = event.clientY - this.touchStartY;
    const startedNearLeftEdge = this.touchStartX <= this.mobileSwipeEdge;
    const isHorizontalGesture = Math.abs(deltaX) >= this.horizontalSwipeThreshold
      && Math.abs(deltaY) <= this.maxVerticalSwipeOffset;
    this.resetTouchGesture();
    if (!isHorizontalGesture) {
      return;
    }
    if (startedNearLeftEdge && deltaX > 0) {
      this.ngZone.run(() => this.commonService.setFullSize(false));
      return;
    }
    if (!this.fullSize && deltaX < 0) {
      this.ngZone.run(() => this.commonService.setFullSize(true));
    }
  }

  @HostListener('document:pointercancel', ['$event'])
  onPointerCancel(_event?: Event): void {
    this.resetTouchGesture();
  }

  private setSidenavMode(): void {
    this.sidenavMode = window.innerWidth < this.mobileBreakpoint ? 'over' : 'side';
  }

  private restoreSidebarPreference(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      this.isSidebarCompact = window.localStorage.getItem(this.sidebarCompactStorageKey) === 'true';
    } catch {
      this.isSidebarCompact = false;
    }
  }

  private persistSidebarPreference(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(this.sidebarCompactStorageKey, String(this.isSidebarCompact));
    } catch {
      // La preferencia visual no debe interrumpir la navegación si el storage está bloqueado.
    }
  }

  private normalizeMenuText(value: string): string {
    return value
      .toLocaleLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  private syncImmersiveMobileRoute(): void {
    this.isImmersiveMobileRoute = this.commonService.isMobileViewport(this.mobileBreakpoint)
      && this.router.url.startsWith('/mensajes');
  }

  private syncMobileBackNavigationGuard(): void {
    if (!this.shouldInterceptMobileBackNavigation()) {
      return;
    }
    this.pushMobileBackNavigationGuard();
  }

  private shouldInterceptMobileBackNavigation(): boolean {
    return this.commonService.isMobileViewport(this.mobileBreakpoint)
      && typeof window !== 'undefined'
      && !!window.history
      && this.authService.loggedIn;
  }

  private pushMobileBackNavigationGuard(): void {
    const currentState = window.history.state || {};
    if (currentState[this.mobileBackGuardStateKey]) {
      return;
    }
    window.history.pushState(
      {
        ...currentState,
        [this.mobileBackGuardStateKey]: true
      },
      '',
      this.router.url
    );
  }

  private isMobileTouchGesture(event: PointerEvent): boolean {
    return event.pointerType === 'touch'
      && event.isPrimary
      && this.commonService.isMobileViewport(this.mobileBreakpoint)
      && typeof window !== 'undefined'
      && !!window.matchMedia
      && window.matchMedia('(hover: none), (pointer: coarse)').matches;
  }

  private resetTouchGesture(): void {
    this.touchStartX = null;
    this.touchStartY = null;
    this.swipeGestureActive = false;
  }

  private setModulesAccess() {
    this.modulesAccess.panel = this.perms.can('panel:view');
    this.modulesAccess.arqueo = this.perms.canAny(['arqueo:sales:view', 'arqueo:saf:view', 'arqueo:clients:view', 'arqueo:fondo:view']);
    this.modulesAccess.rtm = this.perms.can('agenda:view');
    this.modulesAccess.admin = this.perms.can('administracion:view');
    this.modulesAccess.users = this.perms.can('users:view');
    this.modulesAccess.visual = this.perms.can('visual:view');
    this.modulesAccess.history = this.perms.can('history:view');
    this.modulesAccess.prices = this.perms.can('prices:view');
    this.modulesAccess.sales = this.perms.canAny(['sales:view', 'sales:cierre_bol:view']);
    // Inventario es un módulo de consulta disponible para cualquier usuario autenticado.
    this.modulesAccess.inventory = this.perms.canAny(['inventory:view', 'inventory:physical_count:create', 'inventory:physical_count:close', 'inventory:materials:convert']);
    this.modulesAccess.inventoryCatalog = true;
    this.modulesAccess.purchases = this.perms.canAny([...PURCHASE_MODULE_PRELOAD_PERMISSIONS]);
    this.modulesAccess.salesbinder = this.perms.can('salesbinder:manage');
  }

  private navigateToFirstAvailableModule(): void {
    if (this.authService.user.value == null) {
      this.router.navigate(["/login"]);
      return;
    }
    const firstAvailable = this.moduleNavigationOrder.find(
      module => this.modulesAccess[module.access as keyof typeof this.modulesAccess]
    );
    if (firstAvailable) {
      this.router.navigate([firstAvailable.route]);
    }
  }

  redirectTo(uri: string) {
    this.router.navigateByUrl('/dummy', { skipLocationChange: true }).then(() => {
      this.router.navigate([uri]);
    });
  }
}
